// Typed fetch wrappers for the marketing-engine Hono API.
// Same-origin in dev (Vite proxies /crm/api → api.luzgridtech.com).
// Cross-origin in prod (preview.api.* → api.luzgridtech.com), so credentials are required.

import type {
  InboxFolder,
  InboxThreadCard,
  InboxThreadMessages,
  MessageBody,
  Notification,
} from "@/types/api";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    super(message ?? `API ${status}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  // 401 → bounce to OAuth start. Pass full URL as return_to so the worker
  // (after sign-in) can redirect us back to the SPA on preview.api, not to
  // the classic CRM on api.luzgridtech.com.
  if (res.status === 401) {
    const fullReturn = window.location.origin + window.location.pathname + window.location.search;
    const ret = encodeURIComponent(fullReturn);
    window.location.replace(`${API_BASE}/crm/auth/login?return_to=${ret}`);
    throw new ApiError(401, null, "unauthorized");
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) throw new ApiError(res.status, body);

  // Defensive: if Pages' SPA fallback ever serves index.html for an API path
  // (it shouldn't — _redirects proxies /crm/api/* to the worker — but if the
  // proxy is misconfigured we'd silently return HTML typed as T and crash
  // downstream). Throw a clean error instead so the auth gate / query layer
  // can catch it.
  if (!isJson) {
    throw new ApiError(res.status, body, `expected JSON, got ${contentType || "(none)"}`);
  }

  return body as T;
}

// ─── Inbox ────────────────────────────────────────────────────────────

interface ReplyPayload {
  messageId?: string;
  to?: string[];
  cc?: string[];
  body: string;
  subject?: string;
}

export const aiApi = {
  insight(contactId: string): Promise<{ insight: string }> {
    return request(`/crm/api/contacts/${encodeURIComponent(contactId)}/insight`, { method: "POST" });
  },
  coach(dealId: string): Promise<{ coaching: string }> {
    return request(`/crm/api/deals/${encodeURIComponent(dealId)}/coach`, { method: "POST" });
  },
  draftReply(threadId: string, context?: string): Promise<{ draft: string }> {
    return request(`/crm/api/threads/${encodeURIComponent(threadId)}/draft`, {
      method: "POST",
      body: JSON.stringify({ context }),
    });
  },
  morningBrief(): Promise<{ brief: string }> {
    return request(`/crm/api/ai/morning-brief`, { method: "POST" });
  },
  campaignInsights(): Promise<{ insights: string }> {
    return request(`/crm/api/ai/campaign-insights`, { method: "POST" });
  },
};

export const inboxApi = {
  threads(folder: InboxFolder = "Inbox"): Promise<{ threads: InboxThreadCard[] }> {
    return request(`/crm/api/inbox/threads?folder=${encodeURIComponent(folder)}`);
  },

  threadMessages(conversationId: string): Promise<InboxThreadMessages> {
    return request(`/crm/api/inbox/threads/${encodeURIComponent(conversationId)}/messages`);
  },

  messageBody(graphMessageId: string): Promise<MessageBody> {
    return request(`/crm/api/messages/${encodeURIComponent(graphMessageId)}/body`);
  },

  mirrorNow(force = false): Promise<{ ok: boolean; folders?: unknown[] }> {
    return request(`/crm/api/inbox/mirror-now${force ? "?force=1" : ""}`, { method: "POST" });
  },

  reply(payload: ReplyPayload): Promise<{ ok: true; sentFrom: string }> {
    return request(`/crm/api/inbox/reply`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  draft(payload: ReplyPayload): Promise<{ ok: true }> {
    return request(`/crm/api/inbox/draft`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  markRead(threadId: string): Promise<{ ok: true }> {
    return request(`/crm/api/threads/${encodeURIComponent(threadId)}/read`, { method: "POST" });
  },
};

// Inbox snippets — reusable reply text, managed from the composer's
// "Manage snippets…" button.
export interface InboxSnippet {
  id: string;
  name: string;
  body: string;
  is_active: boolean;
  updated_at: string;
}
export const snippetsApi = {
  list(): Promise<{ snippets: InboxSnippet[] }> {
    return request(`/crm/api/inbox/snippets`);
  },
  create(name: string, body: string): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/inbox/snippets`, {
      method: "POST",
      body: JSON.stringify({ name, body }),
    });
  },
  update(id: string, patch: Partial<{ name: string; body: string; is_active: boolean }>): Promise<{ ok: true }> {
    return request(`/crm/api/inbox/snippets/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/inbox/snippets/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// ─── Notifications ────────────────────────────────────────────────────

// The worker's apiNotifications aggregates synthetic items (approvals,
// hot replies, overdue tasks, expiring contracts, AI budget) instead of
// reading a single notifications table — see src/crm/routes/notifications.ts.
// Items don't have row IDs in a single table, so "mark as read" is a UI-only
// concept (the items naturally drop off as their underlying state changes).
export const notificationsApi = {
  async list(): Promise<{ notifications: Notification[] }> {
    const raw = await request<{
      ok?: boolean;
      items?: Array<{
        id: string;
        kind: string;
        title: string;
        detail?: string;
        href?: string;
        at?: string;
        unread?: boolean;
      }>;
    }>(`/crm/api/notifications`);
    const items = raw.items ?? [];
    return {
      notifications: items.map((it) => ({
        id: it.id,
        kind: it.kind,
        title: it.title,
        description: it.detail ?? null,
        action_url: it.href ?? null,
        read_at: it.unread ? null : new Date().toISOString(),
        created_at: it.at ?? new Date().toISOString(),
      })),
    };
  },
  // markRead is now a no-op alias for dismiss; keeping the method so existing
  // call sites don't break, but it physically removes the item.
  async markRead(id: string): Promise<{ ok: true }> {
    await notificationsApi.dismiss(id);
    return { ok: true };
  },
  dismiss(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/notifications/dismiss/${encodeURIComponent(id)}`, { method: "POST" });
  },
  dismissAll(): Promise<{ ok: true; dismissed: number }> {
    return request(`/crm/api/notifications/dismiss-all`, { method: "POST" });
  },
};

// Recycle bin (admin-only): list / restore / purge soft-deleted contacts.
export interface RecycledContact {
  contact_id: string;
  email: string;
  full_name: string | null;
  deleted_at: string;
  deleted_by_user_id: string | null;
  deleted_by_name: string | null;
  expires_at: string;
}
export const recycledContactsApi = {
  list(): Promise<{ items: RecycledContact[] }> {
    return request(`/crm/api/admin/recycled-contacts`);
  },
  restore(id: string): Promise<{
    ok: true;
    restored_contact_id: string;
    notes: number;
    tasks: number;
    activities: number;
    relinked_deals: number;
    relinked_contracts: number;
    kv_keys_restored: number;
  }> {
    return request(`/crm/api/admin/recycled-contacts/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },
  purge(id: string): Promise<{ ok: true; purged: number }> {
    return request(`/crm/api/admin/recycled-contacts/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// Per-user notification preferences (toggles for hot lead email, etc.).
export interface NotificationPrefs {
  hot_lead_email: boolean;
}
export const notificationPrefsApi = {
  get(): Promise<{ prefs: NotificationPrefs; defaults: NotificationPrefs }> {
    return request(`/crm/api/me/notification-prefs`);
  },
  patch(patch: Partial<NotificationPrefs>): Promise<{ ok: true; prefs: NotificationPrefs }> {
    return request(`/crm/api/me/notification-prefs`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
};

// ─── Contacts ─────────────────────────────────────────────────────────

import type {
  ContactsListResponse,
  ContactDetailResponse,
  CrmContact,
  CrmNote,
  CrmTask,
  CrmCompany,
  CrmDeal,
  CrmPipeline,
  CrmContract,
  CrmContractTemplate,
  CrmContractClause,
  CrmContractVariable,
  CrmSow,
  CrmCampaign,
  CrmSequence,
  CrmEmailTemplate,
  CrmApproval,
  ReportsSummary,
  CrmForm,
  CrmSuppression,
  CrmPrivacyRequest,
  CrmUser,
  CrmAuditEntry,
  CrmCostAlert,
  CrmAiSettings,
  CrmApiKey,
  CrmBranding,
} from "@/types/api";

const qs = (params: Record<string, string | number | undefined | null>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const contactsApi = {
  // The pre-existing worker returns { rows, total } from listContacts() — we
  // remap to { contacts, total } so the React side has a consistent shape with
  // companies/deals/contracts. Don't change the worker — classic CRM still
  // reads it as `rows`.
  async list(params: {
    q?: string;
    vertical?: string;
    lifecycle?: string;
    status?: string;
    score?: string;
    sort?: string;
    dir?: "asc" | "desc";
    limit?: number;
    offset?: number;
  } = {}): Promise<ContactsListResponse> {
    const raw = await request<{ rows?: unknown[]; contacts?: unknown[]; total: number; pages?: number }>(
      `/crm/api/contacts${qs(params)}`,
    );
    return {
      contacts: ((raw.rows ?? raw.contacts ?? []) as ContactsListResponse["contacts"]),
      total: raw.total,
      pages: raw.pages,
    };
  },
  // Use the /full endpoint which bundles notes/tasks/activities in one shot.
  get(id: string): Promise<ContactDetailResponse> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/full`);
  },
  create(body: Partial<CrmContact>): Promise<CrmContact> {
    return request(`/crm/api/contacts`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: Partial<CrmContact>): Promise<CrmContact> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  // Hard delete — cascades D1 related rows (notes/tasks/activities/forms/
  // memberships/runs) and purges the cold-sequence KV state. Use archive
  // for soft hide; this is for tear-down (testing flows + GDPR erasure).
  delete(id: string): Promise<{ ok: true; deleted_contact_id: string; purged_email: string | null; kv_overrides_purged: number }> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  reply(id: string, payload: { body: string; subject?: string }): Promise<{ ok: true }> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/reply`, { method: "POST", body: JSON.stringify(payload) });
  },
  addNote(id: string, body: string, isPinned = false): Promise<CrmNote> {
    // Worker expects { body_markdown, is_pinned }, NOT { body }.
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/notes`, {
      method: "POST",
      body: JSON.stringify({ body_markdown: body, is_pinned: isPinned }),
    });
  },
  togglePin(noteId: string): Promise<{ ok: true }> {
    return request(`/crm/api/notes/${encodeURIComponent(noteId)}/pin`, { method: "POST" });
  },
  deleteNote(noteId: string): Promise<{ ok: true }> {
    return request(`/crm/api/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" });
  },
  addTask(id: string, body: Partial<CrmTask>): Promise<CrmTask> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/tasks`, { method: "POST", body: JSON.stringify(body) });
  },
  generateInsight(id: string): Promise<{ insight: string }> {
    return request(`/crm/api/contacts/${encodeURIComponent(id)}/insight`, { method: "POST" });
  },
  bulkArchive(ids: string[], archived = true): Promise<{ ok: true; updated: number }> {
    return request(`/crm/api/contacts/bulk/archive`, {
      method: "POST",
      body: JSON.stringify({ ids, archived }),
    });
  },
  bulkAddToList(ids: string[], listId: string): Promise<{ ok: true; added: number }> {
    return request(`/crm/api/contacts/bulk/add-to-list`, {
      method: "POST",
      body: JSON.stringify({ ids, list_id: listId }),
    });
  },
  bulkEnroll(ids: string[], workflowId: string): Promise<{ ok: true; enrolled: number; errors: { id: string; error: string }[] }> {
    return request(`/crm/api/contacts/bulk/enroll`, {
      method: "POST",
      body: JSON.stringify({ ids, workflow_id: workflowId }),
    });
  },
};

export const contractsBulkApi = {
  trash(ids: string[]): Promise<{ ok: true; updated: number }> {
    return request(`/crm/api/contracts/bulk/trash`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
  },
  restore(ids: string[]): Promise<{ ok: true; updated: number }> {
    return request(`/crm/api/contracts/bulk/trash`, {
      method: "POST",
      body: JSON.stringify({ ids, restore: true }),
    });
  },
};

// ─── Companies ────────────────────────────────────────────────────────

export const companiesApi = {
  list(params: { q?: string; vertical?: string; limit?: number; offset?: number } = {}): Promise<{ companies: CrmCompany[]; total: number }> {
    return request(`/crm/api/companies${qs(params)}`);
  },
  get(id: string): Promise<CrmCompany> {
    return request(`/crm/api/companies/${encodeURIComponent(id)}`);
  },
};

// ─── Deals ────────────────────────────────────────────────────────────

export const dealsApi = {
  list(params: { pipeline_id?: string; owner?: string } = {}): Promise<{ deals: CrmDeal[]; pipelines: CrmPipeline[] }> {
    return request(`/crm/api/deals${qs(params)}`);
  },
  updateStage(id: string, stage_id: string): Promise<{ ok: true }> {
    return request(`/crm/api/deals/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ stage_id }) });
  },
  coach(id: string): Promise<{ coaching: string }> {
    return request(`/crm/api/deals/${encodeURIComponent(id)}/coach`, { method: "POST" });
  },
};

// ─── Contracts ────────────────────────────────────────────────────────

export const contractsApi = {
  list(params: { tab?: string; limit?: number; offset?: number } = {}): Promise<{ contracts: CrmContract[]; total: number }> {
    return request(`/crm/api/contracts${qs(params)}`);
  },
  get(id: string): Promise<CrmContract> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}`);
  },
  templates(): Promise<{ templates: CrmContractTemplate[] }> {
    return request(`/crm/api/contracts/templates`);
  },
  clauses(): Promise<{ clauses: CrmContractClause[] }> {
    return request(`/crm/api/contracts/clauses`);
  },
  variables(): Promise<{ variables: CrmContractVariable[] }> {
    return request(`/crm/api/contracts/variables`);
  },
  // The worker expects { name, template_id }, NOT { title }. Map title→name
  // on the wire so callers can keep the friendlier "title" field.
  create(body: { title: string; template_id?: string; company_id?: string }): Promise<CrmContract> {
    return request(`/crm/api/contracts`, {
      method: "POST",
      body: JSON.stringify({
        name: body.title,
        template_id: body.template_id,
        company_id: body.company_id,
      }),
    });
  },
  update(id: string, body: Partial<CrmContract>): Promise<CrmContract> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  trash(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/trash`, { method: "POST" });
  },
  restore(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/restore`, { method: "POST" });
  },
  duplicate(id: string): Promise<CrmContract> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/duplicate`, { method: "POST" });
  },
  submitForApproval(id: string): Promise<{ ok: true; approval_id: string }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/submit-for-approval`, { method: "POST" });
  },
  approve(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },
  reject(id: string, reason?: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
  },
  // Line items
  addLineItem(id: string, body: Record<string, unknown>): Promise<unknown> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/line-items`, { method: "POST", body: JSON.stringify(body) });
  },
  updateLineItem(id: string, itemId: string, body: Record<string, unknown>): Promise<unknown> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/line-items/${encodeURIComponent(itemId)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  deleteLineItem(id: string, itemId: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/line-items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
  },
  setClauses(id: string, clause_ids: string[]): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/clauses`, { method: "PUT", body: JSON.stringify({ clause_ids }) });
  },
  addComponent(id: string, body: { type: string; title?: string; body?: Record<string, unknown> }): Promise<{ ok: true; id: string; position: number }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components`, { method: "POST", body: JSON.stringify(body) });
  },
  updateComponent(id: string, componentId: string, body: { title?: string; body?: Record<string, unknown> }): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components/${encodeURIComponent(componentId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  deleteComponent(id: string, componentId: string): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components/${encodeURIComponent(componentId)}`, { method: "DELETE" });
  },
  reorderComponents(id: string, ordered_ids: string[]): Promise<{ ok: true }> {
    return request(`/crm/api/contracts/${encodeURIComponent(id)}/components/order`, {
      method: "PUT",
      body: JSON.stringify({ ordered_ids }),
    });
  },
  saveVariable(body: Partial<CrmContractVariable>): Promise<CrmContractVariable> {
    if (body.id) {
      return request(`/crm/api/admin/contract-variables/${encodeURIComponent(body.id)}`, { method: "PATCH", body: JSON.stringify(body) });
    }
    return request(`/crm/api/admin/contract-variables`, { method: "POST", body: JSON.stringify(body) });
  },
  deleteVariable(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/contract-variables/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// ─── SOW ──────────────────────────────────────────────────────────────

export const sowApi = {
  get(id: string): Promise<CrmSow> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}`);
  },
  patch(id: string, body: Partial<CrmSow>): Promise<CrmSow> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  // Worker expects { name, sow_template_id, ... }. Maps title→name for the
  // friendlier API + requires sow_template_id (no blank-SOW path).
  create(body: {
    title: string;
    msp_label?: string;
    sow_template_id: string;
    company_id?: string;
    effective_date?: string;
    term_months?: number;
  }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/sow`, {
      method: "POST",
      body: JSON.stringify({
        name: body.title,
        msp_label: body.msp_label,
        sow_template_id: body.sow_template_id,
        company_id: body.company_id,
        effective_date: body.effective_date,
        term_months: body.term_months,
      }),
    });
  },
  saveTemplate(id: string, name: string): Promise<{ ok: true; template_id: string }> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}/save-template`, { method: "POST", body: JSON.stringify({ name }) });
  },
  // Clone a SOW template into a new editable template. Worker requires
  // { parent_template_id, name } — different from the contract-create path.
  cloneTemplate(parent_template_id: string, name: string): Promise<{ ok: true; id: string; name: string }> {
    return request(`/crm/api/sow/clone-template`, {
      method: "POST",
      body: JSON.stringify({ parent_template_id, name }),
    });
  },
  aiReview(id: string): Promise<{ review: string }> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}/ai-review`, { method: "POST" });
  },
  aiRewriteSection(id: string, sectionId: string, prompt?: string): Promise<{ rewritten: string }> {
    return request(`/crm/api/sow/${encodeURIComponent(id)}/ai-rewrite-section`, { method: "POST", body: JSON.stringify({ sectionId, prompt }) });
  },
};

// ─── Campaigns / Sequences / Templates ───────────────────────────────

import type { SequencesOverview, CampaignsOverview } from "@/types/api";

export const campaignsApi = {
  list(): Promise<{ campaigns: CrmCampaign[] }> {
    return request(`/crm/api/campaigns`);
  },
  overview(): Promise<CampaignsOverview> {
    return request(`/crm/api/campaigns/overview`);
  },
  sequences(): Promise<{ sequences: CrmSequence[] }> {
    return request(`/crm/api/sequences`);
  },
  sequencesOverview(): Promise<SequencesOverview> {
    return request(`/crm/api/sequences/overview`);
  },
  sequenceDetail(id: string): Promise<{
    id: string;
    name: string;
    vertical: string | null;
    language: string | null;
    is_active: boolean;
    total_enrolled: number;
    current_active: number;
    completed: number;
    replied: number;
    steps: Array<{
      step: number;
      day: number;
      subject: string;
      body_markdown: string;
      has_override: boolean;
    }>;
  }> {
    return request(`/crm/api/sequences/${encodeURIComponent(id)}`);
  },
  templates(): Promise<{ templates: CrmEmailTemplate[] }> {
    return request(`/crm/api/email-templates`);
  },
  updateStep(seqId: string, step: number, body: { subject?: string; body_text?: string; body_markdown?: string }): Promise<{ ok: true }> {
    return request(`/crm/api/sequences/${encodeURIComponent(seqId)}/steps/${step}`, { method: "PUT", body: JSON.stringify(body) });
  },
  deleteStep(seqId: string, step: number): Promise<{ ok: true }> {
    return request(`/crm/api/sequences/${encodeURIComponent(seqId)}/steps/${step}`, { method: "DELETE" });
  },
  insights(): Promise<{ insights: string }> {
    return request(`/crm/api/ai/campaign-insights`, { method: "POST" });
  },
};

// ─── Tasks ────────────────────────────────────────────────────────────

export const tasksApi = {
  list(params: { tab?: "open" | "completed" | "overdue"; owner?: string } = {}): Promise<{ tasks: CrmTask[] }> {
    return request(`/crm/api/tasks${qs(params)}`);
  },
  create(body: Partial<CrmTask>): Promise<CrmTask> {
    return request(`/crm/api/tasks`, { method: "POST", body: JSON.stringify(body) });
  },
  complete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}/complete`, { method: "POST" });
  },
  reopen(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}/reopen`, { method: "POST" });
  },
  update(id: string, body: Partial<CrmTask>): Promise<CrmTask> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/tasks/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

// ─── Approvals ────────────────────────────────────────────────────────

export const approvalsApi = {
  list(params: { tab?: "pending" | "recent" | "expired" } = {}): Promise<{ approvals: CrmApproval[] }> {
    return request(`/crm/api/approvals${qs(params)}`);
  },
  approve(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/approvals/${encodeURIComponent(id)}/approve`, { method: "POST" });
  },
  skip(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/approvals/${encodeURIComponent(id)}/skip`, { method: "POST" });
  },
  stop(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/approvals/${encodeURIComponent(id)}/stop`, { method: "POST" });
  },
  edit(id: string, body: { proposed_subject?: string; proposed_body?: string }): Promise<{ ok: true }> {
    return request(`/crm/api/approvals/${encodeURIComponent(id)}/edit`, { method: "POST", body: JSON.stringify(body) });
  },
};

// ─── Reports ──────────────────────────────────────────────────────────

export const reportsApi = {
  summary(): Promise<ReportsSummary> {
    return request(`/crm/api/reports/summary`);
  },
};

// ─── Home dashboard + current user ────────────────────────────────────

import type { HomeData, CurrentUser } from "@/types/api";

export const homeApi = {
  get(): Promise<HomeData> {
    return request(`/crm/api/home`);
  },
};

export const meApi = {
  get(): Promise<CurrentUser> {
    return request(`/crm/api/me`);
  },
};

// ─── Forms / Suppressions / Privacy ───────────────────────────────────

export interface FormField {
  name: string;
  label: string;
  type: "text" | "email" | "phone" | "textarea" | "select";
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

export interface FormSubmission {
  id: string;
  contact_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  values: Record<string, unknown>;
  submitted_at: string;
  is_spam: boolean;
  processed: boolean;
}

export interface FormDetail {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  fields: FormField[];
  submit_button_label: string | null;
  success_message_markdown: string | null;
  success_redirect_url: string | null;
  create_or_update_contact: boolean;
  auto_enroll_workflow_id: string | null;
  notify_emails: string[];
  is_active: boolean;
  views_count: number;
  submissions_count: number;
  conversion_rate: number;
  created_at: string;
  updated_at: string;
  submissions: FormSubmission[];
}

// Saved views — named (filters, columns, sort) tuples scoped to an object_type.
export interface SavedView {
  id: string;
  object_type: string;
  name: string;
  owner_user_id: string | null;
  is_shared: boolean;
  is_default: boolean;
  filters: Record<string, unknown>;
  columns: string[];
  sort: { key: string; dir: "asc" | "desc" } | null;
  created_at: string;
  updated_at: string;
}
export const savedViewsApi = {
  list(objectType: string): Promise<{ views: SavedView[] }> {
    return request(`/crm/api/saved-views?object_type=${encodeURIComponent(objectType)}`);
  },
  create(body: {
    object_type: string;
    name: string;
    is_shared?: boolean;
    is_default?: boolean;
    filters?: unknown;
    columns?: string[];
    sort?: { key: string; dir: "asc" | "desc" } | null;
  }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/saved-views`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, patch: Partial<Omit<SavedView, "id" | "object_type" | "owner_user_id" | "created_at" | "updated_at">>): Promise<{ ok: true }> {
    return request(`/crm/api/saved-views/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/saved-views/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};

export const formsApi = {
  list(): Promise<{ forms: CrmForm[] }> {
    return request(`/crm/api/forms`);
  },
  get(id: string): Promise<FormDetail> {
    return request(`/crm/api/forms/${encodeURIComponent(id)}`);
  },
  create(body: { name: string; description?: string }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/forms`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, patch: Partial<Omit<FormDetail, "id" | "submissions" | "created_at" | "updated_at" | "views_count" | "submissions_count" | "conversion_rate" | "slug">>): Promise<{ ok: true }> {
    return request(`/crm/api/forms/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/forms/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  suppressions(): Promise<{ suppressions: CrmSuppression[] }> {
    return request(`/crm/api/admin/suppressions`);
  },
  addSuppression(body: { email: string; reason: string }): Promise<{ ok: true }> {
    return request(`/crm/api/admin/suppressions`, { method: "POST", body: JSON.stringify(body) });
  },
  removeSuppression(email: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/suppressions/${encodeURIComponent(email)}`, { method: "DELETE" });
  },
};

export const privacyApi = {
  list(params: { tab?: string } = {}): Promise<{ requests: CrmPrivacyRequest[] }> {
    return request(`/crm/api/admin/privacy/requests${qs(params)}`);
  },
  get(id: string): Promise<CrmPrivacyRequest> {
    return request(`/crm/api/admin/privacy/requests/${encodeURIComponent(id)}`);
  },
  verify(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/verify`, { method: "POST" });
  },
  execute(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/execute`, { method: "POST" });
  },
  exportData(id: string): Promise<{ export_url: string }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/export`, { method: "POST" });
  },
  complete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/complete`, { method: "POST" });
  },
  deny(id: string, denial_reason: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/privacy/${encodeURIComponent(id)}/deny`, { method: "POST", body: JSON.stringify({ denial_reason }) });
  },
};

// ─── Admin ────────────────────────────────────────────────────────────

export const adminApi = {
  users(): Promise<{ users: CrmUser[] }> {
    return request(`/crm/api/admin/users`);
  },
  audit(params: { limit?: number; offset?: number } = {}): Promise<{ entries: CrmAuditEntry[] }> {
    return request(`/crm/api/admin/audit${qs(params)}`);
  },
  costs(): Promise<import("@/types/api").AdminCostsResponse> {
    return request(`/crm/api/admin/costs`);
  },
  refreshCosts(): Promise<{ updated: boolean }> {
    return request(`/crm/api/admin/costs/refresh`, { method: "POST" });
  },
  costAlertCreate(body: Partial<CrmCostAlert>): Promise<CrmCostAlert> {
    return request(`/crm/api/admin/cost-alerts`, { method: "POST", body: JSON.stringify(body) });
  },
  costAlertToggle(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/cost-alerts/${encodeURIComponent(id)}/toggle`, { method: "POST" });
  },
  costAlertDelete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/cost-alerts/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  aiSettings(): Promise<CrmAiSettings> {
    return request(`/crm/api/admin/ai/settings`);
  },
  aiSettingsUpdate(body: Partial<CrmAiSettings>): Promise<{ ok: true }> {
    return request(`/crm/api/admin/ai/settings`, { method: "POST", body: JSON.stringify(body) });
  },
  apiKeys(): Promise<{ keys: CrmApiKey[] }> {
    return request(`/crm/api/admin/api-keys`);
  },
  apiKeyCreate(body: { name: string; scope: string }): Promise<{ key: string; id: string }> {
    return request(`/crm/api/admin/api-keys`, { method: "POST", body: JSON.stringify(body) });
  },
  apiKeyRevoke(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/admin/api-keys/${encodeURIComponent(id)}/revoke`, { method: "POST" });
  },
  branding(): Promise<CrmBranding> {
    return request(`/crm/api/admin/branding`);
  },
  brandingUpdate(body: Partial<CrmBranding>): Promise<{ ok: true }> {
    return request(`/crm/api/admin/branding`, { method: "POST", body: JSON.stringify(body) });
  },
  diagnose(what: string): Promise<{ ok: boolean; result?: unknown; error?: string }> {
    return request(`/crm/api/admin/diagnose/${encodeURIComponent(what)}`);
  },
  diagnoseAll(): Promise<Record<string, { ok: boolean; result?: unknown; error?: string }>> {
    return request(`/crm/api/admin/diagnose`);
  },
  integrations(): Promise<{ integrations: Array<{ name: string; status: string; details?: string }> }> {
    return request(`/crm/api/admin/integrations`);
  },
};

// ─── Workflows + Lists ────────────────────────────────────────────────

import type { CrmWorkflow, CrmList, CrmListDetail } from "@/types/api";

export const workflowsApi = {
  list(params: { status?: string } = {}): Promise<{ workflows: CrmWorkflow[] }> {
    return request(`/crm/api/workflows${qs(params)}`);
  },
  get(id: string): Promise<CrmWorkflow> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}`);
  },
  create(body: Partial<CrmWorkflow>): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/workflows`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: Partial<CrmWorkflow>): Promise<{ ok: true }> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  archive(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  aiDraft(prompt: string): Promise<{ ok: true; draft: Partial<CrmWorkflow> }> {
    return request(`/crm/api/workflows/draft`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
    });
  },
  enroll(id: string, contactId: string): Promise<{ ok: true; run_id: string }> {
    return request(`/crm/api/workflows/${encodeURIComponent(id)}/enroll`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId }),
    });
  },
};

export const listsApi = {
  list(): Promise<{ lists: CrmList[] }> {
    return request(`/crm/api/lists`);
  },
  get(id: string): Promise<CrmListDetail> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}`);
  },
  create(body: Partial<CrmList>): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/lists`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: Partial<CrmList>): Promise<{ ok: true }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  delete(id: string): Promise<{ ok: true }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  addMember(id: string, contactId: string): Promise<{ ok: true }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}/members`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId }),
    });
  },
  recompute(id: string): Promise<{ ok: true; member_count: number }> {
    return request(`/crm/api/lists/${encodeURIComponent(id)}/recompute`, {
      method: "POST",
    });
  },
};

// Client portal — staff side. Public portal SPA hits a different endpoint.
export interface ProjectTemplate {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  stages: Array<{
    id: string;
    position: number;
    label: string;
    description: string | null;
    icon: string | null;
    required_action: string | null;
  }>;
}

export interface CrmProject {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: "active" | "completed" | "archived" | "on_hold";
  template_id: string | null;
  current_stage_id: string | null;
  company_id: string | null;
  company_name: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  stage_count: number;
  completed_stages: number;
  stakeholder_count: number;
}

export interface CrmProjectStage {
  id: string;
  project_id: string;
  position: number;
  label: string;
  description: string | null;
  icon: string | null;
  required_action: string | null;
  contract_id: string | null;
  form_id: string | null;
  file_id: string | null;
  status: "pending" | "active" | "completed" | "skipped";
  completed_at: string | null;
}

export interface CrmProjectStakeholder {
  contact_id: string;
  email: string;
  full_name: string;
  role: string | null;
  invited_at: string;
  first_seen_at: string | null;
}

export const projectsApi = {
  templates(): Promise<{ templates: ProjectTemplate[] }> {
    return request(`/crm/api/project-templates`);
  },
  list(params: { status?: string; company_id?: string } = {}): Promise<{ projects: CrmProject[] }> {
    return request(`/crm/api/projects${qs(params)}`);
  },
  get(idOrSlug: string): Promise<{
    project: CrmProject;
    stages: CrmProjectStage[];
    stakeholders: CrmProjectStakeholder[];
    tasks: Array<Record<string, unknown>>;
  }> {
    return request(`/crm/api/projects/${encodeURIComponent(idOrSlug)}`);
  },
  create(body: {
    name: string;
    description?: string;
    company_id: string;
    template_id?: string;
    stakeholder_contact_ids?: string[];
    send_invites?: boolean;
  }): Promise<{ ok: true; id: string; slug: string; invites_sent: number; invite_errors: { email: string; error: string }[] }> {
    return request(`/crm/api/projects`, { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, patch: Partial<{ name: string; description: string; status: string; owner_user_id: string }>): Promise<{ ok: true }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },
  invite(id: string, contactIds?: string[]): Promise<{ ok: true; sent: number; errors: { email: string; error: string }[] }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}/invite`, {
      method: "POST",
      body: JSON.stringify({ contact_ids: contactIds ?? [] }),
    });
  },
  addStakeholder(id: string, contactId: string, role?: string, sendInvite = true): Promise<{ ok: true; invite_sent?: boolean; errors?: { email: string; error: string }[] }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}/stakeholders`, {
      method: "POST",
      body: JSON.stringify({ contact_id: contactId, role, send_invite: sendInvite }),
    });
  },
  createTask(id: string, body: {
    title: string;
    description_markdown?: string;
    stage_id?: string;
    assigned_to_contact_id?: string;
    assigned_to_user_id?: string;
    due_at?: string;
  }): Promise<{ ok: true; id: string }> {
    return request(`/crm/api/projects/${encodeURIComponent(id)}/tasks`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

// ─── Search ───────────────────────────────────────────────────────────

export const searchApi = {
  global(q: string): Promise<{ contacts: CrmContact[]; companies: CrmCompany[]; deals: CrmDeal[] }> {
    return request(`/crm/api/search?q=${encodeURIComponent(q)}`);
  },
  ai(q: string): Promise<{ result: string; matches?: unknown[] }> {
    return request(`/crm/api/ai/search`, { method: "POST", body: JSON.stringify({ q }) });
  },
};

// ─── Auth ─────────────────────────────────────────────────────────────

export const authApi = {
  // The worker doesn't currently expose a "/me" JSON endpoint; we infer
  // session validity by hitting any auth-gated endpoint and watching for 401.
  // /api/notifications is cheap and already exists.
  async ping(): Promise<boolean> {
    try {
      await notificationsApi.list();
      return true;
    } catch {
      return false;
    }
  },
};
